/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import { injectIntl } from 'react-intl';
import { Dropdown, notification, Upload } from 'antd';
import { CheckOutlined, DeleteFilled, DownOutlined, PaperClipOutlined } from '@ant-design/icons';
import axios from "axios";
import * as smsCounter from "@marcinkowalczyk/due-sms-counter";
import * as smartEncode from "@marcinkowalczyk/smartencoding";
import SelectSender from "../../../chat/SelectSender/SelectSender";
import SelectCanned from "../../../chat/SelectCanned";
import IntlMessages from "../../../../util/IntlMessages";
import { getCountryCode } from "../../../../util/helpers";
import { getStoredEnterToSend } from "../../../../util/uiPreferences";
import { chooseEnterToSend, fetchUserPreferences } from "../../../../util/userPreferences";
import './TypingArea.less';

const TypingArea = ({
                        message,
                        updateTypingAreaValue,
                        updateMmsFile,
                        submitComment,
                        isArchive,
                        handleAutoComplete,
                        cannedResponses,
                        senderNumber,
                        clientNumber,
                        phoneNumbersAll,
                        updateSenderNumber,
                        focusTypingArea,
                        intl
}) => {

  const unicodeMaxLength= 396;
  const nonUnicodeMaxLength = 912;
  const [smsCount, setSmsCount] = useState(0);
  const [isUnicode, setIsUnicode] = useState(false);
  const [mmsFile, setMmsFile] = useState();
  const [mmsLimitations, setMmsLimitations] = useState();
  const isMmsAvailable = ['US', 'CA'].includes(getCountryCode(clientNumber));
  const textareaRef = useRef(null);
  // Caret position to restore after we insert a line break ourselves (set before the next key).
  const pendingCaretRef = useRef(null);
  useLayoutEffect(() => {
      if (pendingCaretRef.current === null || !textareaRef.current) return;
      textareaRef.current.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current);
      pendingCaretRef.current = null;
  }, [message]);
  // Grow the box with its text. On desktop a fixed CSS height (!important) wins over this.
  useLayoutEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
  }, [message]);
  // Enter sends (default) or adds a new line; the user's choice, saved to their account.
  const [enterToSend, setEnterToSend] = useState(() => getStoredEnterToSend() ?? true);

  useEffect(() => {
      if (getStoredEnterToSend() !== null) return undefined;
      let cancelled = false;
      fetchUserPreferences().then(prefs => {
          if (!cancelled && typeof prefs?.enterToSend === 'boolean') setEnterToSend(prefs.enterToSend);
      });
      return () => {
          cancelled = true;
      };
  }, []);

  const changeEnterToSend = value => {
      setEnterToSend(value);
      chooseEnterToSend(value);
  };

  useEffect(async () => {
      if (isMmsAvailable) {
          const {data} = await axios.get(`${process.env.REACT_APP_API_URL}/conversations/mms-limitations`);
          setMmsLimitations({
              ...data,
              mmsAvailableContentTypes: data.mmsAvailableContentTypes?.filter(item => item !== 'text/plain')
          });
      }

      const smsStatus = smsCounter.calcCharAndSmsCount(smartEncode.remove(message));
      setSmsCount(message.length ? smsStatus.sms_count : 0);
      setIsUnicode(smsStatus.is_unicode);

      if(smsStatus.is_unicode) {
          updateTypingAreaValue(message.substring(0, unicodeMaxLength));
      }
  }, [message]);

    const uploadProps = {
        beforeUpload: file => {
            if (!mmsLimitations.mmsAvailableContentTypes.includes(file.type)) {
                notification.error({message: intl.formatMessage({id: 'chats.mmsInvalidContentType'}, {value: mmsLimitations.mmsAvailableContentTypes?.join(", ")})});
                return false;
            }

            if (mmsLimitations.mmsMaxFileSize < file.size) {
                notification.error({message: intl.formatMessage({id: 'chats.mmsInvalidFileSize'}, {value: mmsLimitations.mmsMaxFileSize / 1024})});
                return false;
            }

            setMmsFile(file);
            updateMmsFile(file);
            return false;
        },
        fileList: []
    };

    const removeMmsFile = () => {
        setMmsFile(undefined);
        updateMmsFile(undefined);
    };

    const send = () => {
        submitComment();
        removeMmsFile();
    };

    // A line break at the caret (Ctrl+Enter, and Shift+Enter or Enter when they don't send).
    const insertNewLine = textarea => {
        const start = textarea.selectionStart ?? message.length;
        const end = textarea.selectionEnd ?? start;
        pendingCaretRef.current = start + 1;
        updateTypingAreaValue(`${message.slice(0, start)}\n${message.slice(end)}`);
    };

    // Enter sends or adds a new line (user's choice); Shift+Enter does the other one;
    // Ctrl+Enter (Cmd+Enter on Mac) always adds a new line.
    const onKeyDown = event => {
        if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.altKey) return;
        event.preventDefault();
        if (event.ctrlKey || event.metaKey) {
            insertNewLine(event.target);
            return;
        }
        const sends = event.shiftKey ? !enterToSend : enterToSend;
        if (sends) send();
        else insertNewLine(event.target);
    };

    const sendOptions = [
        {
            key: 'send',
            label: (
                <div className="send-option">
                    <span className="send-option__check">{enterToSend ? <CheckOutlined/> : null}</span>
                    <span>
                        <b>{intl.formatMessage({id: 'chats.enterSends', defaultMessage: 'Enter sends the message'})}</b>
                        <small>{intl.formatMessage({id: 'chats.enterSendsHint', defaultMessage: 'Shift+Enter or Ctrl+Enter adds a new line'})}</small>
                    </span>
                </div>
            )
        },
        {
            key: 'newline',
            label: (
                <div className="send-option">
                    <span className="send-option__check">{enterToSend ? null : <CheckOutlined/>}</span>
                    <span>
                        <b>{intl.formatMessage({id: 'chats.enterNewLine', defaultMessage: 'Enter adds a new line'})}</b>
                        <small>{intl.formatMessage({id: 'chats.enterNewLineHint', defaultMessage: 'Shift+Enter sends; Ctrl+Enter adds a new line'})}</small>
                    </span>
                </div>
            )
        }
    ];

  return isArchive ? (
    <div className="gx-chat-main-footer is-archive"/>
  ) : (
    <div className="gx-chat-main-footer typing-area">
      <div className="gx-flex-row gx-align-items-center text-area-wrapper">
        <div className="gx-col col-text-area">
          <div className="gx-form-group">
            <textarea
              ref={textareaRef}
              id="required" className="gx-border-0 ant-input gx-chat-textarea"
              onChange={(event) => updateTypingAreaValue(event.target.value)}
              onKeyDown={onKeyDown}
              onSelect={() => focusTypingArea()}
              value={message}
              placeholder={intl.formatMessage({id: 'chats.textMessage'})}
              maxLength={isUnicode ? unicodeMaxLength : nonUnicodeMaxLength}
            />
          </div>
        </div>
      </div>

      <div className="gx-flex-row gx-align-items-center short-list" style={{maxHeight: 51}}>
        <div className='typing-area--justify-flex-left'>
            {isMmsAvailable && mmsLimitations ? (
              <Upload
                  {...uploadProps}
                  maxCount={1}
                  accept={mmsLimitations.mmsAvailableContentTypes?.join(", ")}
              >
                <i className='icon icon-attachment gx-icon-btn z-index-2 ant-dropdown-trigger' />
              </Upload>
            ) : null}

          <SelectCanned
            cannedResponses={cannedResponses}
            handleAutoComplete={handleAutoComplete}
          />
          <SelectSender
            selectedValue={senderNumber}
            senderList={phoneNumbersAll}
            updateSenderNumber={updateSenderNumber}
          />
        </div>
        <div className="typing-area__send-group">
            <div className='typing-area__send' role="button" tabIndex={0} onClick={send}
                 onKeyDown={event => (event.key === ' ' || event.key === 'Enter') && (event.preventDefault(), send())}>
                <IntlMessages id={mmsFile ? "chats.sendMms" : "chats.send"}/>
            </div>
            <Dropdown
                trigger={['click']}
                placement="topRight"
                overlayClassName="send-options-menu"
                menu={{
                    items: sendOptions,
                    selectable: true,
                    selectedKeys: [enterToSend ? 'send' : 'newline'],
                    onClick: ({key}) => changeEnterToSend(key === 'send')
                }}
            >
                <button type="button" className="typing-area__send-more"
                        aria-label={intl.formatMessage({id: 'chats.sendOptions', defaultMessage: 'Send options'})}>
                    <DownOutlined/>
                </button>
            </Dropdown>
        </div>
      </div>
      <div className="sms-count">
          {mmsFile ? (
              <>
                  <span><PaperClipOutlined/>{mmsFile.name}</span>
                  <DeleteFilled theme="filled" className="delete-icon" onClick={() => removeMmsFile()}/>
              </>
          ) : (
            <>
                <IntlMessages id="conversation.smsCount"/>:
                <span className="value">{smsCount}</span>
            </>
          )}
      </div>
    </div>
  );
};

export default injectIntl(TypingArea);
